import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { ReloadOutlined, CloseOutlined } from '@ant-design/icons';

interface UpdateNotificationProps {
  isVisible: boolean;
  onUpdate: () => void;
  onDismiss?: () => void;
}

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const NotificationContainer = styled.div<{ $isVisible: boolean; $isExiting: boolean }>`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 999999;
  display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
  animation: ${({ $isExiting }) => ($isExiting ? slideDown : slideUp)} 0.3s ease-out forwards;
`;

const NotificationCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid rgba(30, 98, 211, 0.3);
  border-radius: 12px;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  backdrop-filter: blur(10px);
  max-width: 420px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
    margin: 0 16px;
    padding: 16px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #1E62D3 0%, #4a90e2 100%);
  border-radius: 10px;
  flex-shrink: 0;
  
  svg {
    font-size: 20px;
    color: white;
  }
  
  @media (max-width: 480px) {
    display: none;
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  
  @media (max-width: 480px) {
    text-align: center;
  }
`;

const Title = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: 0.01em;
`;

const Description = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  
  @media (max-width: 480px) {
    margin-left: 0;
    width: 100%;
    justify-content: center;
  }
`;

const UpdateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #1E62D3 0%, #2d7be8 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: linear-gradient(135deg, #2d7be8 0%, #4a90e2 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(30, 98, 211, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    font-size: 14px;
  }
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.8);
    border-color: rgba(255, 255, 255, 0.25);
  }
  
  svg {
    font-size: 12px;
  }
`;

export const UpdateNotification: React.FC<UpdateNotificationProps> = ({
  isVisible,
  onUpdate,
  onDismiss,
}) => {
  const [showNotification, setShowNotification] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShowNotification(true);
      setIsExiting(false);
    }
  }, [isVisible]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      setShowNotification(false);
      onDismiss?.();
    }, 300);
  };

  if (!showNotification) return null;

  return (
    <NotificationContainer $isVisible={showNotification} $isExiting={isExiting}>
      <NotificationCard>
        <IconWrapper>
          <ReloadOutlined />
        </IconWrapper>
        <TextContent>
          <Title>New Version Available</Title>
          <Description>HELM has been updated. Refresh to get the latest features.</Description>
        </TextContent>
        <ButtonGroup>
          <UpdateButton onClick={onUpdate}>
            <ReloadOutlined />
            Refresh Now
          </UpdateButton>
          {onDismiss && (
            <DismissButton onClick={handleDismiss} aria-label="Dismiss notification">
              <CloseOutlined />
            </DismissButton>
          )}
        </ButtonGroup>
      </NotificationCard>
    </NotificationContainer>
  );
};

export default UpdateNotification;


